import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { destinations, destinationComparison } from "@/data/destinations";

const Destinations = () => (
  <div data-testid="destinations-page">
    <SEO
      title="UAE Destinations — Dubai, Abu Dhabi & Ras Al Khaimah"
      description="Compare UAE property destinations: Dubai property investment for UK buyers, Abu Dhabi family lifestyle communities and Al Marjan Island property investment in Ras Al Khaimah."
      path="/destinations"
      keywords="best areas to invest in Dubai 2026, Al Marjan Island property investment, Abu Dhabi property"
    />
    <PageHero
      eyebrow="Destinations"
      title="Where in the UAE does your story begin?"
      intro="Three emirates, three distinct rhythms of life and investment. Start with the lifestyle — the investment logic follows."
      testid="destinations-hero"
    />

    <section className="pb-24" data-testid="destinations-grid">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
        {destinations.map((d, i) => (
          <Reveal key={d.slug} delay={i * 0.12}>
            <Link
              to={`/destinations/${d.slug}`}
              data-testid={`destination-card-${d.slug}`}
              className="card-hover group relative block h-[460px] rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.15)]"
            >
              <img src={d.cardImage} alt={`${d.name} property and lifestyle`} loading="lazy" className="card-img absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/35 to-transparent" />
              <div className="absolute bottom-0 p-7">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#E6CA65] mb-2">{d.cardStat}</p>
                <h2 className="font-display text-3xl text-[#F4F3EF] mb-3">{d.name}</h2>
                <p className="text-sm text-[#c9cdd6] font-light line-clamp-3">{d.intro}</p>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C5A880] mt-4 group-hover:gap-3.5 transition-all duration-300">
                  Explore {d.name} <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="pb-24" data-testid="destination-comparison-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Compare the Emirates</p>
          <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] mb-3">Dubai vs Abu Dhabi vs Ras Al Khaimah</h2>
          <p className="text-[#9DA3AE] font-light mb-10">{destinationComparison.caption}</p>
        </Reveal>
        <Reveal>
          <div className="overflow-x-auto rounded-2xl border border-[rgba(197,168,128,0.2)]" data-testid="destination-comparison-table">
            <table className="w-full text-left min-w-[760px]">
              <thead>
                <tr className="bg-[#13151D]">
                  {destinationComparison.head.map((h) => (
                    <th key={h} className="px-6 py-4 text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {destinationComparison.rows.map((row, ri) => (
                  <tr key={ri} className="border-t border-[rgba(197,168,128,0.1)] hover:bg-[#13151D]/60 transition-colors">
                    {row.map((cell, ci) => (
                      <td key={ci} className={`px-6 py-4 text-sm ${ci === 0 ? "text-[#F4F3EF] font-medium" : "text-[#9DA3AE] font-light"}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal>
          <p className="text-sm text-[#9DA3AE] font-light leading-relaxed mt-10 max-w-3xl">
            Still weighing it up? Read our guides to{" "}
            <Link to="/insights/buying-uae-property-as-a-uk-citizen" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">buying property in Dubai from the UK</Link>,{" "}
            <Link to="/insights/what-is-freehold-property-uae" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">freehold property in the UAE</Link> and{" "}
            <Link to="/insights/uae-golden-visa-through-property" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">the UAE Golden Visa through property</Link>, or explore{" "}
            <Link to="/destinations/ras-al-khaimah" className="text-[#E6CA65] underline underline-offset-4 decoration-[rgba(230,202,101,0.4)] hover:decoration-[#E6CA65] transition-colors">Al Marjan Island property investment</Link>{" "}
            in depth.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand testid="destinations-cta" />
  </div>
);

export default Destinations;
